import { useSelector } from 'react-redux'
import {
  fetchEntityDetailAndProducts,
  selectEntityStatus,
  selectLocation,
  selectMapEntityDetails,
  selectMapEntityVariant,
  selectPestPacLocationPin,
  setPestPacLocationDetail,
  setPestPacLocationPin,
  withPestPacKnockStatus,
} from '@user/store/map'
import { useCallback, useEffect, useState } from 'react'
import { EntityDetail, EntityLocationDTO } from '@user/store/map/typings'
import { useAppDispatch } from '@sales-center/common'
import { getAllKnockStatuses } from '@user/store/knockStatus/api'
import { fetchPestPacLocationDetail } from '@user/store/map/api'

type Props = {
  onKnockStatusClick: (location: EntityLocationDTO) => void
  onViewKnockStatusHistory?: (location: EntityLocationDTO) => void
}

function useData(props: Props) {
  const { onKnockStatusClick, onViewKnockStatusHistory } = props
  const dispatch = useAppDispatch()
  const [entityDetails, setEntityDetails] = useState<EntityDetail | null>(null)
  const rawEntityDetails = useSelector(selectMapEntityDetails)
  const entityStatus = useSelector(selectEntityStatus)
  const serviceLocation = useSelector(selectLocation)
  const variant = useSelector(selectMapEntityVariant) as any
  const pestPacPin = useSelector(selectPestPacLocationPin)

  useEffect(() => {
    setEntityDetails(rawEntityDetails)
  }, [rawEntityDetails])

  async function refetchEntityDetails(
    noLoading: boolean = true,
  ): Promise<EntityDetail | null> {
    if (!entityDetails) return null
    const result = await dispatch(
      fetchEntityDetailAndProducts([
        variant,
        entityDetails.id,
        serviceLocation?.id || 0,
        noLoading,
      ]),
    )
    if (
      fetchEntityDetailAndProducts.fulfilled.match(result) &&
      result.payload
    ) {
      setEntityDetails(result.payload)
      return result.payload
    }
    return null
  }

  const refetchPestPacDetail = useCallback(async () => {
    const locationId = pestPacPin?.pestPacLocationId
    if (!locationId) return

    try {
      const detail = await fetchPestPacLocationDetail(locationId)
      dispatch(setPestPacLocationDetail(detail))
      dispatch(
        setPestPacLocationPin(withPestPacKnockStatus(pestPacPin, detail)),
      )
    } catch {
      // Leave the card as it is rather than blanking it on a transient failure.
    }
  }, [pestPacPin, dispatch])

  const handleViewKnockStatusHistory = useCallback(() => {
    if (pestPacPin) {
      dispatch(getAllKnockStatuses())
      onViewKnockStatusHistory?.(pestPacPin)
      return
    }

    if (!serviceLocation) return

    const location = {
      latitude: serviceLocation.latitude,
      longitude: serviceLocation.longitude,
      serviceLocationId: serviceLocation.id,
      entityId: entityDetails?.id,
      entityType: variant,
      contactLocationId: serviceLocation.contactLocationId,
    } as EntityLocationDTO

    dispatch(getAllKnockStatuses())
    onViewKnockStatusHistory?.(location)
  }, [
    pestPacPin,
    serviceLocation,
    entityDetails?.id,
    variant,
    onViewKnockStatusHistory,
    dispatch,
  ])

  const handleKnockStatusClick = useCallback(
    (location: EntityLocationDTO) => {
      dispatch(getAllKnockStatuses())
      onKnockStatusClick(location)
    },
    [onKnockStatusClick, dispatch],
  )

  const handleDrawerKnockStatusClick = useCallback(() => {
    if (pestPacPin) {
      handleKnockStatusClick(pestPacPin)
      return
    }

    if (!serviceLocation) return

    const location = {
      latitude: serviceLocation.latitude,
      longitude: serviceLocation.longitude,
      serviceLocationId: serviceLocation.id,
      entityId: entityDetails?.id,
      entityType: variant,
      contactLocationId: serviceLocation.contactLocationId,
    } as EntityLocationDTO

    handleKnockStatusClick(location)
  }, [
    pestPacPin,
    serviceLocation,
    entityDetails,
    variant,
    handleKnockStatusClick,
  ])

  return {
    entityDetails,
    entityStatus,
    serviceLocation,
    variant,
    pestPacPin,
    refetchEntityDetails,
    refetchPestPacDetail,
    handleKnockStatusClick,
    handleViewKnockStatusHistory,
    handleDrawerKnockStatusClick,
  }
}

export default useData
